/** TanStack Query hooks for project Sources, compiled wiki pages, and packed context */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  ContextResult,
  PushSourceInput,
  PushSourceResult,
  SourceDetail,
  SourceStatus,
  SourceSummary,
  WikiCompileEstimate,
  WikiCompileJob,
  WikiPageDetail,
  WikiPageSummary,
  WikiProposalDetail,
  WikiProposalSummary,
  WikiWriteResult,
} from '@agentwiki/shared'
import { apiClient } from '../lib/api-client'
import { projectKeys } from './use-projects'

/** Query keys live under projects/content so they never collide with list/detail keys */
const contentKey = (project: string) => [...projectKeys.all, 'content', project] as const

function projectPath(project: string, suffix = '') {
  return `/api/projects/${encodeURIComponent(project)}${suffix}`
}

export function useSources(project: string, params: { status: SourceStatus | 'all'; search?: string }) {
  const qs = new URLSearchParams({ status: params.status, limit: '200' })
  if (params.search) qs.set('search', params.search)
  return useQuery({
    queryKey: [...contentKey(project), 'sources', params],
    queryFn: () => apiClient.get<{ sources: SourceSummary[]; total: number }>(projectPath(project, `/sources?${qs}`)),
    enabled: Boolean(project),
  })
}

export function useSource(project: string, id: string | undefined) {
  return useQuery({
    queryKey: [...contentKey(project), 'source', id],
    queryFn: () => apiClient.get<SourceDetail>(projectPath(project, `/sources/${encodeURIComponent(id!)}`)),
    enabled: Boolean(project && id),
  })
}

export function usePushSource(project: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PushSourceInput) => apiClient.post<PushSourceResult>(projectPath(project, '/sources'), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentKey(project) })
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(project) })
      queryClient.invalidateQueries({ queryKey: projectKeys.list() })
    },
  })
}

/** Archive an active Source or restore an archived one */
export function useSetSourceArchived(project: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => apiClient.post<{ source: SourceSummary }>(
      projectPath(project, `/sources/${encodeURIComponent(id)}/${archived ? 'archive' : 'restore'}`),
      {},
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentKey(project) })
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(project) })
      queryClient.invalidateQueries({ queryKey: projectKeys.list() })
    },
  })
}

export function useWikiPages(project: string) {
  return useQuery({
    queryKey: [...contentKey(project), 'wiki'],
    queryFn: () => apiClient.get<{ pages: WikiPageSummary[] }>(projectPath(project, '/wiki')),
    enabled: Boolean(project),
  })
}

export function useWikiPage(project: string, slug: string | undefined) {
  return useQuery({
    queryKey: [...contentKey(project), 'wiki', slug],
    queryFn: () => apiClient.get<WikiPageDetail>(projectPath(project, `/wiki/${encodeURIComponent(slug!)}`)),
    enabled: Boolean(project && slug),
  })
}

const COMPILE_POLL_MS = 3000

function isOpen(job: WikiCompileJob | undefined): boolean {
  return job?.status === 'queued' || job?.status === 'running'
}

/** Cost, balance, and provider of a server compile right now */
export function useCompileEstimate(project: string) {
  return useQuery({
    queryKey: [...contentKey(project), 'compile', 'estimate'],
    queryFn: () => apiClient.get<WikiCompileEstimate>(projectPath(project, '/compile/estimate')),
    enabled: Boolean(project),
  })
}

/**
 * Latest server compile; polls while it runs, then refreshes the wiki, queue, estimate, and credit balance.
 * `activeJobId` (from the estimate) starts polling for a compile started elsewhere (CLI, MCP, another tab).
 */
export function useLatestCompileJob(project: string, activeJobId: string | null = null) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: [...contentKey(project), 'compile', 'latest'],
    queryFn: async () => {
      const { jobs } = await apiClient.get<{ jobs: WikiCompileJob[] }>(projectPath(project, '/compile/jobs?limit=1'))
      const previous = queryClient.getQueryData<WikiCompileJob | null>([...contentKey(project), 'compile', 'latest'])
      const latest = jobs[0] ?? null
      if (previous && isOpen(previous) && latest && !isOpen(latest)) {
        void queryClient.invalidateQueries({ queryKey: [...contentKey(project), 'wiki'] })
        void queryClient.invalidateQueries({ queryKey: [...contentKey(project), 'proposals'] })
        void queryClient.invalidateQueries({ queryKey: [...contentKey(project), 'compile', 'estimate'] })
        void queryClient.invalidateQueries({ queryKey: ['credits'] })
      }
      return latest
    },
    enabled: Boolean(project),
    refetchInterval: (query) => {
      const latest = query.state.data ?? undefined
      const followingOther = Boolean(activeJobId) && (latest?.id !== activeJobId || isOpen(latest))
      return isOpen(latest) || followingOther ? COMPILE_POLL_MS : false
    },
  })
}

export function useStartCompile(project: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post<WikiCompileJob>(projectPath(project, '/compile'), {}),
    onSuccess: (job) => {
      queryClient.setQueryData([...contentKey(project), 'compile', 'latest'], job)
      void queryClient.invalidateQueries({ queryKey: [...contentKey(project), 'compile', 'estimate'] })
      void queryClient.invalidateQueries({ queryKey: ['credits'] })
    },
  })
}

export function useProjectContext(project: string) {
  return useMutation({
    mutationFn: (input: { task: string; budget: number; includeSources: boolean }) =>
      apiClient.post<ContextResult>(projectPath(project, '/context'), input),
  })
}

export type ProposalStatusFilter = 'pending' | 'applied' | 'discarded' | 'all'

export function useWikiProposals(project: string, status: ProposalStatusFilter) {
  return useQuery({
    queryKey: [...contentKey(project), 'proposals', status],
    queryFn: () => apiClient.get<{ proposals: WikiProposalSummary[]; pending: number }>(
      projectPath(project, `/proposals?status=${status}&limit=100`),
    ),
    enabled: Boolean(project),
  })
}

export function useWikiProposal(project: string, id: string | undefined) {
  return useQuery({
    queryKey: [...contentKey(project), 'proposal', id],
    queryFn: () => apiClient.get<WikiProposalDetail>(projectPath(project, `/proposals/${encodeURIComponent(id!)}`)),
    enabled: Boolean(project && id),
  })
}

/** Add (apply) or discard a proposal; refreshes the queue and the wiki */
export function useDecideProposal(project: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, decision, note }: { id: string; decision: 'apply' | 'discard'; note?: string }) =>
      apiClient.post<WikiWriteResult | WikiProposalSummary>(
        projectPath(project, `/proposals/${encodeURIComponent(id)}/${decision}`),
        note ? { note } : {},
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: contentKey(project) }),
  })
}

export function useSetWikiPins(project: string, slug: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { anchor: string; pinned: boolean }) =>
      apiClient.post<WikiPageDetail>(projectPath(project, `/wiki/${encodeURIComponent(slug)}/pins`), input),
    onSuccess: (page) => queryClient.setQueryData([...contentKey(project), 'wiki', slug], page),
  })
}
